// The only module that touches browser storage (RC1, enforced by web/.oxlintrc.json). It keeps UI
// preferences from the typed allowlist below, unsaved drafts in the slots of DRAFT_SLOTS, and this
// tab's media grants (when each team's media cookie ends, and whether the API said cookies are
// off: hints for the first paint, which the CDN still checks), and nothing else: never the team,
// the brand, a role, a balance, a job or any other fact the server owns. A new preference is one
// field in Prefs and one entry in PREFS; tsc rejects either without the other. A new draft is one
// entry in DRAFT_SLOTS.
import { z } from "zod";

import { CREATED_ID } from "./themes/family";

export interface Prefs {
  /** Unsaved onboarding input only; URL values win when sign-in opens another tab. */
  firstAdChoice: { template: string; product: string; manual: boolean } | null;
  /** The UI this browser opens: Classic (the default until Pro covers everything), Simple or Pro
   *  (spec ui-perf/ui-switch-classic-simple-pro). Only the header's pill sets it. */
  ui: "classic" | "simple" | "pro";
  colorScheme: "light" | "dark" | "auto";
  /** The theme family this browser shows, by id (spec web/themes-engine); Created.ai by default. */
  theme: string;
  /** The free daily case's sounds: on, as Daniil chose, until muted (spec web/case-opening). */
  caseSound: boolean;
  /** The awards whose unlock card this browser has shown, newest last, so a reload never plays one
   *  twice (spec web/achievements, 4). */
  achievementCards: string[];
  /** The sidebar open (240px) or compact (70px); compact by default, as the live app starts. */
  sidebarOpen: boolean;
  /** Pro's nav open (240px) or its compact rail (70px), as the sidebar; open by default, as Pro's
   *  design draws it (spec ui-perf/ui-switch-classic-simple-pro, PR e8). */
  proNavOpen: boolean;
  /** The library's and the products page's gallery column counts, 2 to 12. */
  libraryColumns: number;
  productColumns: number;
  /** The library's layout (spec library-rows): live's masonry grid, or its rows. */
  libraryLayout: "masonry" | "rows";
  /** The library's rows' height, live's Small 150, Medium 200 or Large 250. */
  libraryRowHeight: 150 | 200 | 250;
  /** The Sounds page's grid column count, live's 2, 4, 6, 8 or 10. */
  soundColumns: number;
  actorColumns: number;
  /** The editor's timeline snaps a dragged or trimmed edge (live's "Attach", N); on by default. */
  timelineSnapping: boolean;
  /** The editor's preview axis (S): the canvas shows the frame under the pointer; off by default. */
  previewAxis: boolean;
  /** What the editor's preview plays: each video's 540p copy (by default), or its original. */
  previewQuality: "copies" | "original";
}

/** How many shown unlock cards a browser remembers: far more than a person earns between visits. */
export const CARDS_KEPT = 200;

const PREFS: { [N in keyof Prefs]: { schema: z.ZodType<Prefs[N]>; fallback: Prefs[N] } } = {
  firstAdChoice: {
    schema: z.object({
      template: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(64),
      product: z.string().max(1500), manual: z.boolean(),
    }).nullable(), fallback: null,
  },
  ui: { schema: z.enum(["classic", "simple", "pro"]), fallback: "classic" },
  // Light by default, as the owner uses the live app. index.html's inline script reads the same
  // key and falls back to the same value before the bundle loads, so a dark choice does not
  // flash white; prefs.test.ts keeps the two in step.
  colorScheme: { schema: z.enum(["light", "dark", "auto"]), fallback: "light" },
  // A family's id; one that no family has shows Created.ai (themes/families.ts).
  theme: { schema: z.string().regex(/^[a-z0-9-]{1,40}$/), fallback: CREATED_ID },
  caseSound: { schema: z.boolean(), fallback: true },
  achievementCards: { schema: z.array(z.string().uuid()).max(CARDS_KEPT), fallback: [] },
  sidebarOpen: { schema: z.boolean(), fallback: false },
  proNavOpen: { schema: z.boolean(), fallback: true },
  libraryColumns: { schema: z.number().int().min(2).max(12), fallback: 6 },
  productColumns: { schema: z.number().int().min(2).max(12), fallback: 6 },
  // The library's display settings (spec library-rows): live's grid, and its Medium rows.
  libraryLayout: { schema: z.enum(["masonry", "rows"]), fallback: "masonry" },
  libraryRowHeight: { schema: z.union([z.literal(150), z.literal(200), z.literal(250)]), fallback: 200 },
  soundColumns: {
    schema: z
      .number()
      .int()
      .min(2)
      .max(10)
      .refine((count) => count % 2 === 0),
    fallback: 6,
  },
  // Live's AI Actors grid: 2 to 10 by two, 6 by default (`useAdaptiveGridUiSettings`).
  actorColumns: {
    schema: z
      .number()
      .int()
      .min(2)
      .max(10)
      .refine((count) => count % 2 === 0),
    fallback: 6,
  },
  // The editor's two timeline switches (spec capcut-shortcuts.md): today's snapping, no axis.
  timelineSnapping: { schema: z.boolean(), fallback: true },
  previewAxis: { schema: z.boolean(), fallback: false },
  // The preview's quality pill (spec editor/proxy-playback.md): the copies, as card 23's option A.
  previewQuality: { schema: z.enum(["copies", "original"]), fallback: "copies" },
};

export type PrefName = keyof Prefs;
export type PrefValue<N extends PrefName> = Prefs[N];

export const KEY_PREFIX = "cai.prefs.";

// This tab's view of every preference it has read or written. Storage can throw (Safari private
// mode, a full quota, a blocked origin); the memory copy keeps the app working when it does.
const memory = new Map<PrefName, unknown>();

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function parse<N extends PrefName>(name: N, raw: string | null): PrefValue<N> {
  const pref = PREFS[name];
  if (raw === null) return pref.fallback;
  try {
    const result = pref.schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : pref.fallback;
  } catch {
    return pref.fallback;
  }
}

export function readPref<N extends PrefName>(name: N): PrefValue<N> {
  if (memory.has(name)) return memory.get(name) as PrefValue<N>;
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(KEY_PREFIX + name) ?? null;
  } catch {
    raw = null;
  }
  const value = parse(name, raw);
  memory.set(name, value);
  return value;
}

export function writePref<N extends PrefName>(name: N, value: PrefValue<N>): void {
  memory.set(name, value);
  try {
    storage()?.setItem(KEY_PREFIX + name, JSON.stringify(value));
  } catch {
    // The memory copy already holds the value for this tab.
  }
}

export function clearPref(name: PrefName): void {
  memory.delete(name);
  try {
    storage()?.removeItem(KEY_PREFIX + name);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Calls `listener` when another tab changes `name`. Returns the unsubscribe function. */
export function subscribePref<N extends PrefName>(
  name: N,
  listener: (value: PrefValue<N>) => void,
): () => void {
  const key = KEY_PREFIX + name;
  const onStorage = (event: StorageEvent): void => {
    // A null key means another tab cleared all of storage.
    if (event.key !== key && event.key !== null) return;
    const value = parse(name, event.key === null ? null : event.newValue);
    memory.set(name, value);
    listener(value);
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** Test hook: forget this tab's memory copy, as a fresh page load would. */
export function resetPrefsMemory(): void {
  memory.clear();
}

// Drafts: unsaved work a screen keeps in this browser per key (a viewer and a subject), until it is
// saved or no longer fits. Platform code cannot import a feature's types, so the feature passes the
// schema its draft must meet; a stored draft that fails it, or is not JSON, reads as none. Drafts
// have no memory copy: one that storage cannot keep is simply not kept.
const DRAFT_SLOTS = {
  /** Live's layer editor (spec G13b): the composition and the selection, per viewer and job. */
  layerEditor: "cai.drafts.layer-editor.",
  /** Its AI edits started here: where each is to land, per viewer and Layerize. */
  layerEdits: "cai.drafts.layer-edits.",
} as const;

export type DraftSlot = keyof typeof DRAFT_SLOTS;

/** The draft kept in `slot` under `key`, or null when there is none or it fails `schema`. */
export function readDraftSlot<T>(slot: DraftSlot, key: string, schema: z.ZodType<T>): T | null {
  let raw: string | null;
  try {
    raw = storage()?.getItem(DRAFT_SLOTS[slot] + key) ?? null;
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const result = schema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function writeDraftSlot<T>(slot: DraftSlot, key: string, value: T): void {
  try {
    storage()?.setItem(DRAFT_SLOTS[slot] + key, JSON.stringify(value));
  } catch {
    // A full or blocked storage keeps no draft; the screen works on without one.
  }
}

/** Drops the draft kept in `slot` under `key`. */
export function clearDraftSlot(slot: DraftSlot, key: string): void {
  try {
    storage()?.removeItem(DRAFT_SLOTS[slot] + key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Every key in `slot` that holds a draft, without the slot's prefix. */
export function draftSlotKeys(slot: DraftSlot): string[] {
  const prefix = DRAFT_SLOTS[slot];
  const keys: string[] = [];
  try {
    const store = storage();
    for (let at = 0; store !== undefined && at < store.length; at += 1) {
      const key = store.key(at);
      if (key?.startsWith(prefix)) keys.push(key.slice(prefix.length));
    }
  } catch {
    return [];
  }
  return keys;
}

// Media grants (spec assets/media-cookies, Design 4), in `sessionStorage`, this tab's alone:
// - when this tab's media cookie of a team ends, in `Date.now()` milliseconds, per user and team,
//   so a reload or a team opened again paints at once, asking for bare URLs, instead of waiting on
//   the grant's POST. Keyed by the user too, and all cleared at sign-in and sign-out, so another
//   user in this tab never asks on this one's grant;
// - that the API answered `enabled: false`, so a team's first paint does not wait on the POST
//   while cookies are off.
// A grant the browser lost (cleared cookies) is caught by the media access's retry, never trusted
// further.
const GRANT_PREFIX = "media-access:";
const GRANTS_OFF = `${GRANT_PREFIX}off`;

function tabStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function grantKey(userId: string, teamId: string): string {
  return `${GRANT_PREFIX}${userId.toLowerCase()}:${teamId.toLowerCase()}`;
}

/** When `userId`'s media cookie of `teamId` ends in this tab, or null with none kept. */
export function readMediaGrant(userId: string, teamId: string): number | null {
  try {
    const raw = tabStorage()?.getItem(grantKey(userId, teamId)) ?? null;
    const endsAt = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(endsAt) ? endsAt : null;
  } catch {
    return null;
  }
}

export function writeMediaGrant(userId: string, teamId: string, endsAt: number): void {
  try {
    tabStorage()?.setItem(grantKey(userId, teamId), String(endsAt));
  } catch {
    // A full or blocked storage keeps no grant: the next paint waits on the POST.
  }
}

/** Drops `userId`'s kept grant of `teamId`: the API said cookies are off. */
export function forgetMediaGrant(userId: string, teamId: string): void {
  try {
    tabStorage()?.removeItem(grantKey(userId, teamId));
  } catch {
    // Nothing to drop when storage is unavailable.
  }
}

/** Whether the API's last answer in this tab said media cookies are off. */
export function readMediaGrantsOff(): boolean {
  try {
    return tabStorage()?.getItem(GRANTS_OFF) === "1";
  } catch {
    return false;
  }
}

export function writeMediaGrantsOff(off: boolean): void {
  try {
    if (off) tabStorage()?.setItem(GRANTS_OFF, "1");
    else tabStorage()?.removeItem(GRANTS_OFF);
  } catch {
    // Not kept: the next first paint waits on the POST.
  }
}

/** Drops every media grant of this tab, and the word that cookies are off: a sign-in and a sign-out. */
export function clearMediaGrants(): void {
  try {
    const store = tabStorage();
    const keys: string[] = [];
    for (let at = 0; store !== undefined && at < store.length; at += 1) {
      const key = store.key(at);
      if (key?.startsWith(GRANT_PREFIX)) keys.push(key);
    }
    for (const key of keys) store?.removeItem(key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
